/**
 * Client half of the wonda web app's gateway-exchange session.
 *
 * All backend-contract coupling lives here so a contract tweak touches one
 * function each:
 *  - GET  /session/token   (product host, same-origin) → { accessToken, expiresAt }
 *  - GET  /oauth/userinfo  (API origin) → identity (snake_case)
 *  - GET  /auth/start      (product host) → redirect to the gateway login
 *  - POST /auth/logout     (product host) → revoke + clear cookies + Tank sign-out
 *
 * The refresh token is an HttpOnly cookie the server rotates transparently, so
 * "signed in?" is simply "does /session/token yield a token?".
 */
import { getApiBaseUrl } from "@/common/api";

/** Raw /oauth/userinfo response (snake_case per the API contract). */
type UserinfoResponse = {
  account_id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  image_url?: string | null;
  admin?: boolean;
  onboarded?: boolean;
  /** ISO-8601 account creation time; absent on older API builds. */
  created_at?: string | null;
};

/**
 * Identity resolved from /oauth/userinfo, shaped to the fields the ~15
 * existing useUser() consumers read (user.fullName,
 * user.primaryEmailAddress.emailAddress, user.imageUrl, user.hasImage,
 * user.publicMetadata.accountId/admin).
 */
export type GatewayIdentity = {
  id: string;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string;
  hasImage: boolean;
  /**
   * Account creation time, read by the Google Ads signup-conversion check.
   * Null when the API omits or sends an unparseable value.
   */
  createdAt: Date | null;
  primaryEmailAddress: { emailAddress: string } | null;
  publicMetadata: {
    accountId: string;
    admin: boolean;
    /** Canonical userinfo flag. */
    onboarded: boolean;
    /**
     * Legacy alias of `onboarded`, kept so the existing account route (which
     * reads publicMetadata.cliWelcomeCompleted) works untouched.
     */
    cliWelcomeCompleted: boolean;
  };
};

/**
 * useUser().user shape: GatewayIdentity plus a reload() (refetches userinfo),
 * kept for the one seam consumer that calls user.reload() (onboarding
 * GuideExperience).
 */
export type SessionUser = GatewayIdentity & {
  reload: () => Promise<void>;
};

/** useUser()-shaped discriminated state: { isLoaded, isSignedIn, user }. */
export type SessionUserState =
  | { isLoaded: boolean; isSignedIn: false; user: null }
  | { isLoaded: true; isSignedIn: true; user: SessionUser };

/** Router-context auth object the app reads. */
export type SessionAuth = {
  isSignedIn: boolean;
  userId: string | null;
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
};

// ── Session hint ─────────────────────────────────────────────────────────────

// One-bit "a session likely exists here" marker for optimistic first paint:
// the nav reads it to show a loading avatar instead of flashing sign-in
// buttons at a returning user while /session/token round-trips. Purely a
// rendering hint — token presence stays the only signed-in source of truth,
// and a wrong hint self-corrects as soon as presence resolves.
const SESSION_HINT_STORAGE_KEY = "wonda:session-hint";

/** Best-effort read of the local session hint (false when storage is unavailable). */
export function checkHasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function setSessionHint(present: boolean): void {
  try {
    if (present) localStorage.setItem(SESSION_HINT_STORAGE_KEY, "1");
    else localStorage.removeItem(SESSION_HINT_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode, blocked) — the hint is best-effort.
  }
}

// ── Access token (short-lived; server refreshes via HttpOnly cookie) ─────────

type CachedToken = { accessToken: string; expiresAt: number };

let cachedToken: CachedToken | null = null;
let inflightToken: Promise<string | null> | null = null;

// Refresh slightly before expiry so a token is never sent right as it lapses.
const TOKEN_EXPIRY_BUFFER_MS = 5_000;
// Fallback TTL when the server omits expiresAt (should not happen per contract).
const FALLBACK_TOKEN_TTL_MS = 60_000;

/**
 * Returns the current gateway access token, or null when signed out. Caches to
 * expiry and de-dupes concurrent callers behind a single in-flight fetch.
 *
 * ASSUMPTION (backend contract): expiresAt is treated as epoch MILLISECONDS. If
 * the backend emits seconds, the cache simply disables (expiresAt lands in 1970
 * so every call refetches) — still correct, just an extra request per call.
 */
export async function getGatewaySessionToken(options?: {
  skipCache?: boolean;
}): Promise<string | null> {
  const skipCache = options?.skipCache === true;
  if (
    !skipCache &&
    cachedToken &&
    Date.now() < cachedToken.expiresAt - TOKEN_EXPIRY_BUFFER_MS
  ) {
    return cachedToken.accessToken;
  }
  // A skipCache caller must observe a genuinely fresh token, so it never reuses
  // an in-flight fetch that may have started before the state it needs to see
  // (e.g. the onboarding provisioning-race beater). It kicks its own request.
  if (!skipCache && inflightToken) return inflightToken;
  const fetchPromise = requestSessionToken().finally(() => {
    // Only clear the shared slot if it's still ours — a later fetch may have
    // replaced it.
    if (inflightToken === fetchPromise) inflightToken = null;
  });
  inflightToken = fetchPromise;
  return fetchPromise;
}

async function requestSessionToken(): Promise<string | null> {
  try {
    const res = await fetch("/session/token", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      cachedToken = null;
      setSessionHint(false);
      return null;
    }
    const body = (await res.json()) as {
      accessToken?: string;
      expiresAt?: number;
    };
    if (!body.accessToken) {
      cachedToken = null;
      setSessionHint(false);
      return null;
    }
    cachedToken = {
      accessToken: body.accessToken,
      expiresAt: body.expiresAt ?? Date.now() + FALLBACK_TOKEN_TTL_MS,
    };
    setSessionHint(true);
    return body.accessToken;
  } catch {
    // Network failure says nothing about the session, so the hint is kept.
    cachedToken = null;
    return null;
  }
}

// ── Identity ─────────────────────────────────────────────────────────────────

// A present access token is the source of truth for "signed in"; userinfo may
// still be loading or transiently failing. So a userinfo error is retried (a
// degraded-but-signed-in state), never conflated with "signed out".
const USERINFO_MAX_ATTEMPTS = 3;
const USERINFO_RETRY_BASE_MS = 300;

/** Whether a gateway session exists — the authoritative signed-in signal. */
export async function hasGatewaySession(): Promise<boolean> {
  return (await getGatewaySessionToken()) != null;
}

/**
 * Fetches /oauth/userinfo with the current access token and maps it to the
 * identity shape. Returns null when signed out (no token).
 * When a token IS present, a transient userinfo failure is retried before
 * giving up, so route guards don't read a blip as "not this identity".
 * Callable imperatively (route beforeLoad guards) or from the React hook.
 */
export async function fetchGatewayIdentity(): Promise<GatewayIdentity | null> {
  const token = await getGatewaySessionToken();
  if (!token) return null;
  for (let attempt = 0; attempt < USERINFO_MAX_ATTEMPTS; attempt++) {
    const identity = await requestUserinfo(token);
    if (identity) return identity;
    if (attempt < USERINFO_MAX_ATTEMPTS - 1) {
      await sleep(USERINFO_RETRY_BASE_MS * (attempt + 1));
    }
  }
  return null;
}

/**
 * Admin-access decision for a route guard, distinguishing a userinfo FAILURE
 * from a confirmed non-admin so a real admin isn't locked out of /admin when
 * userinfo is transiently unavailable:
 *  - "admin"           userinfo resolved, admin flag set → allow
 *  - "not-admin"       userinfo resolved, admin flag unset → hide (notFound)
 *  - "unavailable"     token present but userinfo failed (retries exhausted) →
 *                      authenticated-but-degraded; surface a retryable error
 *  - "unauthenticated" no session token at all
 */
export type GatewayAdminAccess =
  | "admin"
  | "not-admin"
  | "unavailable"
  | "unauthenticated";

export async function resolveGatewayAdminAccess(): Promise<GatewayAdminAccess> {
  const identity = await fetchGatewayIdentity();
  if (identity) return identity.publicMetadata.admin ? "admin" : "not-admin";
  return (await hasGatewaySession()) ? "unavailable" : "unauthenticated";
}

/**
 * React-Query variant of the identity fetch. null = signed out (a valid result,
 * no retry); a userinfo error THROWS so React Query retries it, which surfaces
 * as a still-loading identity rather than flipping the user to signed-out on a
 * transient failure.
 */
export async function fetchGatewayIdentityForQuery(): Promise<GatewayIdentity | null> {
  const token = await getGatewaySessionToken();
  if (!token) return null;
  const identity = await requestUserinfo(token);
  if (!identity) throw new Error("userinfo unavailable");
  return identity;
}

/** Single /oauth/userinfo attempt. Returns null on any failure. */
async function requestUserinfo(token: string): Promise<GatewayIdentity | null> {
  try {
    const res = await fetch(`${getOAuthApiOrigin()}/oauth/userinfo`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as UserinfoResponse;
    return normalizeUserinfo(body);
  } catch {
    return null;
  }
}

/** Maps the snake_case /oauth/userinfo response to the identity shape. */
/** Tolerates absent/garbage values: a bad timestamp must not break identity. */
function parseCreatedAt(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function normalizeUserinfo(info: UserinfoResponse): GatewayIdentity {
  const firstName = info.first_name ?? null;
  const lastName = info.last_name ?? null;
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || null;
  const imageUrl = info.image_url ?? "";
  const email = info.email ?? "";
  return {
    id: info.account_id,
    fullName,
    firstName,
    lastName,
    imageUrl,
    hasImage: imageUrl.length > 0,
    createdAt: parseCreatedAt(info.created_at),
    primaryEmailAddress: email ? { emailAddress: email } : null,
    publicMetadata: {
      accountId: info.account_id,
      admin: info.admin ?? false,
      onboarded: info.onboarded ?? false,
      cliWelcomeCompleted: info.onboarded ?? false,
    },
  };
}

// ── Login / logout ───────────────────────────────────────────────────────────

/**
 * Same-origin entry point that starts the login. The product host's
 * GET /auth/start mints PKCE + CSRF state and redirects to Tank's /authorize
 * with client_id=wonda-web. When `returnTo` is given it is forwarded as
 * ?redirect_url= so the backend can land the user back on the page they were
 * headed to after login.
 */
export function getGatewayLoginUrl(
  returnTo?: string,
  /**
   * "signup" marks a registration entry (the /signup shim): while signups are
   * waitlist-gated the backend short-circuits to Tank's /signup page (the
   * waitlist panel) instead of the /authorize sign-in flow.
   */
  screen?: "signup",
  /** An email already typed on the site; /auth/start forwards it to Tank as login_hint. */
  loginHint?: string,
  /**
   * An org-invitation signup: the invited email is pre-approved, so Tank
   * opens its sign-up form instead of the join panel.
   */
  orgInvite?: boolean,
): string {
  const params = new URLSearchParams();
  if (returnTo) params.set("redirect_url", returnTo);
  if (screen) params.set("screen", screen);
  if (loginHint) params.set("login_hint", loginHint);
  if (orgInvite) params.set("org_invite", "1");
  const query = params.toString();
  return query ? `/auth/start?${query}` : "/auth/start";
}

/**
 * Signs the user fully out of the gateway-exchange session. A fetch would
 * swallow the cross-origin 302 and leave the Tank identity session alive (the
 * next protected nav would silently re-mint a product session from it), so
 * this does a TOP-LEVEL form POST navigation that follows the whole chain:
 *   POST /auth/logout (revoke + clear product cookies)
 *     -> 302 to Tank /logout?return_to=... (clears the identity-origin cookie)
 *       -> back to the product, signed out.
 * Clears the local token cache first so nothing serves a stale bearer
 * mid-teardown.
 */
export function logoutGatewaySession(): void {
  cachedToken = null;
  setSessionHint(false);
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/auth/logout";
  document.body.appendChild(form);
  form.submit();
}

// OAuth endpoints live at the API origin root, not under the /api base path the
// rest of the app uses (mirrors the OAuth consent page).
function getOAuthApiOrigin(): string {
  return new URL(getApiBaseUrl()).origin;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
